import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Wallet,
  ReceiptText,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Hash,
  BarChart3,
} from 'lucide-react';
import { accountService } from '@services/accountService';
import api from '@services/api';
import Button from '@components/common/Button';
import Card from '@components/common/Card';
import Loading from '@components/common/Loading';
import { formatCurrency, formatDate } from '@utils/formatters';
import { ACCOUNT_TYPE_LABELS, TRANSACTION_TYPE_LABELS } from '@utils/constants';
import { clsx } from 'clsx';

// ─── MetricCard ────────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, valueColor, icon, iconBg }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <div className={clsx('p-2.5 rounded-xl', iconBg)}>{icon}</div>
    </div>
    <p className={clsx('text-2xl font-bold', valueColor)}>{value}</p>
  </div>
);

// ─── TransactionRow ────────────────────────────────────────────────────────────
const TransactionRow = ({ transaction }) => {
  const isIncome = transaction.type === 'income';
  const isTransfer = transaction.type === 'transfer';

  return (
    <div className="flex items-center gap-4 py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
      {/* Icon */}
      <div
        className={clsx(
          'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
          isIncome
            ? 'bg-emerald-100 dark:bg-emerald-900/30'
            : isTransfer
            ? 'bg-blue-100 dark:bg-blue-900/30'
            : 'bg-red-100 dark:bg-red-900/30'
        )}
      >
        {isIncome ? (
          <ArrowUpRight size={16} className="text-emerald-600 dark:text-emerald-400" />
        ) : isTransfer ? (
          <ArrowLeftRight size={16} className="text-blue-600 dark:text-blue-400" />
        ) : (
          <ArrowDownRight size={16} className="text-red-600 dark:text-red-400" />
        )}
      </div>

      {/* Description + Category */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {transaction.description}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {transaction.categoryName || TRANSACTION_TYPE_LABELS[transaction.type] || transaction.type}
          {' · '}
          {formatDate(transaction.date)}
        </p>
      </div>

      {/* Amount */}
      <p
        className={clsx(
          'text-sm font-semibold flex-shrink-0',
          isIncome
            ? 'text-emerald-600 dark:text-emerald-400'
            : isTransfer
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-red-600 dark:text-red-400'
        )}
      >
        {isIncome ? '+' : isTransfer ? '' : '-'}
        {formatCurrency(Math.abs(transaction.amount))}
      </p>
    </div>
  );
};

// ─── AccountDetail ─────────────────────────────────────────────────────────────
const AccountDetail = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();

  const {
    data: accountData,
    isLoading: isLoadingAccount,
    isError: isAccountError,
  } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountService.getById(accountId),
    enabled: !!accountId,
  });

  const {
    data: statementData,
    isLoading: isLoadingStatement,
  } = useQuery({
    queryKey: ['account-statement', accountId],
    queryFn: () => api.get(`/accounts/${accountId}/statement`),
    enabled: !!accountId,
  });

  if (isLoadingAccount) {
    return <Loading text="Carregando conta..." />;
  }

  if (isAccountError || !accountData) {
    return (
      <div className="space-y-6 pb-8">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/app/accounts')}
          >
            Voltar
          </Button>
        </div>
        <Card>
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            Conta não encontrada.
          </p>
        </Card>
      </div>
    );
  }

  const account = accountData.account ?? accountData;
  const statement = statementData?.statement ?? statementData ?? {};
  const transactions = statement.transactions ?? [];
  const totalIncome = statement.totalIncome ?? 0;
  const totalExpense = statement.totalExpense ?? 0;
  const transactionCount = statement.transactionCount ?? transactions.length;

  const avgTransaction =
    transactionCount > 0
      ? (totalIncome + Math.abs(totalExpense)) / transactionCount
      : 0;

  return (
    <div className="space-y-6 pb-8">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/app/accounts')}
        >
          Voltar
        </Button>
      </div>

      {/* Account Header Card */}
      <div
        className="relative overflow-hidden rounded-2xl text-white shadow-xl p-6"
        style={{
          background: account.color
            ? `linear-gradient(135deg, ${account.color}cc 0%, ${account.color}88 100%)`
            : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        }}
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />

        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            >
              {account.icon || <Wallet size={28} className="text-white" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{account.name}</h1>
              <p className="text-white/70 text-sm mt-0.5">
                {ACCOUNT_TYPE_LABELS[account.type] ?? account.type}
              </p>
            </div>
          </div>

          <div className="sm:text-right">
            <p className="text-white/60 text-xs mb-1">Saldo atual</p>
            <p className="text-4xl font-bold tracking-tight">
              {formatCurrency(account.currentBalance ?? 0)}
            </p>
            {account.initialBalance !== undefined && (
              <p className="text-white/50 text-xs mt-1">
                Saldo inicial: {formatCurrency(account.initialBalance)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Receitas"
          value={formatCurrency(totalIncome)}
          valueColor="text-emerald-600 dark:text-emerald-400"
          iconBg="bg-emerald-100 dark:bg-emerald-900/30"
          icon={<TrendingUp size={16} className="text-emerald-600 dark:text-emerald-400" />}
        />
        <MetricCard
          title="Despesas"
          value={formatCurrency(Math.abs(totalExpense))}
          valueColor="text-red-600 dark:text-red-400"
          iconBg="bg-red-100 dark:bg-red-900/30"
          icon={<TrendingDown size={16} className="text-red-600 dark:text-red-400" />}
        />
        <MetricCard
          title="Transações"
          value={transactionCount}
          valueColor="text-gray-900 dark:text-white"
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          icon={<Hash size={16} className="text-blue-600 dark:text-blue-400" />}
        />
        <MetricCard
          title="Média por Transação"
          value={formatCurrency(avgTransaction)}
          valueColor="text-gray-900 dark:text-white"
          iconBg="bg-purple-100 dark:bg-purple-900/30"
          icon={<BarChart3 size={16} className="text-purple-600 dark:text-purple-400" />}
        />
      </div>

      {/* Transactions */}
      <Card
        title="Extrato"
        subtitle="Últimas movimentações desta conta"
        headerAction={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ReceiptText size={15} />}
            onClick={() =>
              navigate(`/app/transactions?accountId=${accountId}`)
            }
          >
            Ver todas
          </Button>
        }
      >
        {isLoadingStatement ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <ReceiptText size={32} className="text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nenhuma transação encontrada para esta conta.
            </p>
          </div>
        ) : (
          <div>
            {transactions.map((tx) => (
              <TransactionRow key={tx.transactionId ?? tx.id} transaction={tx} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AccountDetail;
